const BOOLEAN_TRUE = new Set(['true', '1'])
const BOOLEAN_FALSE = new Set(['false', '0'])

export function compareVetServerOrder() {
  return 0
}

export function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

export function queryText(query, key, maxLength = 200) {
  const value = String(firstQueryValue(query?.[key]) ?? '').trim()
  return value ? value.slice(0, maxLength) : ''
}

export function queryInteger(query, key, allowed = []) {
  const source = firstQueryValue(query?.[key])
  if (source == null || source === '') return null
  const value = Number(source)
  if (!Number.isInteger(value)) return null
  return allowed.length && !allowed.includes(value) ? null : value
}

export function queryBoolean(query, key) {
  const value = String(firstQueryValue(query?.[key]) ?? '').toLowerCase()
  if (BOOLEAN_TRUE.has(value)) return true
  if (BOOLEAN_FALSE.has(value)) return false
  return null
}

export function queryDateTime(query, key) {
  const value = queryText(query, key, 30)
  return /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/.test(value) ? value : ''
}

export function normalizeVetDateTimeDayRange(start, end) {
  const datePart = (source) => {
    const value = String(source || '').trim()
    const match = /^(\d{4}-\d{2}-\d{2})(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/.exec(value)
    return match?.[1] || ''
  }
  const first = datePart(start)
  const second = datePart(end)
  if (!first && !second) return []
  const left = first || second
  const right = second || first
  const [rangeStart, rangeEnd] = left <= right ? [left, right] : [right, left]
  return [`${rangeStart}T00:00:00`, `${rangeEnd}T23:59:59`]
}

export function dateFilterModelToDateTimeDayRange(model = {}) {
  return normalizeVetDateTimeDayRange(model?.dateFrom, model?.dateTo)
}

export function queryDateTimeRange(query, startKey, endKey) {
  const rawStart = queryText(query, startKey, 30)
  const rawEnd = queryText(query, endKey, 30)
  return normalizeVetDateTimeDayRange(rawStart, rawEnd)
}

export function queryDate(query, key) {
  const value = queryText(query, key, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
}

export function normalizeVetRouteRange(start, end) {
  if (!start && !end) return []
  const left = start || end
  const right = end || start
  return left <= right ? [left, right] : [right, left]
}

export function routeFilterSignature(route, acceptedKeys = []) {
  const source = acceptedKeys.reduce((result, key) => {
    const value = firstQueryValue(route?.query?.[key])
    if (value != null && value !== '') result[key] = String(value)
    return result
  }, {})
  return `${route?.path || ''}?${new URLSearchParams(source).toString()}`
}

export function resolvedPageResourceAvailable(router, path) {
  const expectedPath = String(path || '')
  const resolved = router?.resolve?.({ path })
  return Boolean(resolved?.matched?.some((record) =>
    String(record.path || '') === expectedPath
      && record.meta?.resourceId
      && !record.meta?.resourceUnavailable
  ))
}

export function textFilterModel(value) {
  return value ? { filterType: 'text', type: 'contains', filter: value } : null
}

export function selectFilterModel(value) {
  return value == null || value === '' ? null : { value }
}

export function dateFilterModel(start, end) {
  if (!start && !end) return null
  if (start && end && start !== end) {
    return { filterType: 'date', type: 'inRange', dateFrom: start, dateTo: end }
  }
  return { filterType: 'date', type: 'equals', dateFrom: start || end }
}

export const ANIMAL_CASE_ROUTE_KEYS = Object.freeze([
  'caseNo', 'animalKeyword', 'visitTimeStart', 'visitTimeEnd', 'diseaseIdFk',
  'attendingVetIdFk', 'treatmentOutcome', 'isContagious', 'isKeyEvent', 'status',
  'dataSource', 'latestConfirmedInChain',
])

export const PRESCRIPTION_ROUTE_KEYS = Object.freeze([
  'prescriptionNo', 'caseNo', 'animalKeyword', 'diagnosisName', 'prescribingVetIdFk',
  'prescriptionTimeStart', 'prescriptionTimeEnd', 'status', 'dispensingStatus', 'hasRemaining',
])

export const DEATH_REPORT_ROUTE_KEYS = Object.freeze([
  'deathReportNo', 'animalKeyword', 'deathTimeStart', 'deathTimeEnd', 'caseNo',
  'diseaseIdFk', 'attendingVetIdFk', 'reportVetIdFk', 'status',
])

export const INBOUND_ROUTE_KEYS = Object.freeze([
  'inboundRecordNo', 'inboundRecordName', 'status', 'inboundUserIdFk',
  'inboundTimeStart', 'inboundTimeEnd',
])

export const OUTBOUND_ROUTE_KEYS = Object.freeze([
  'outboundRecordNo', 'outboundRecordName', 'prescriptionNo', 'caseNo', 'animalKeyword',
  'receiverUserIdFk', 'dispenseUserIdFk', 'outboundTimeStart', 'outboundTimeEnd', 'status',
])

export const ADJUSTMENT_ROUTE_KEYS = Object.freeze([
  'adjustmentRecordNo', 'adjustmentRecordName', 'adjustmentType', 'status',
  'adjustmentUserIdFk', 'adjustmentTimeStart', 'adjustmentTimeEnd',
])

export const INVENTORY_ROUTE_KEYS = Object.freeze([
  'tab', 'categoryIdFk', 'medicineId', 'genericName', 'isEnable', 'isLowStock',
  'batchNo', 'productionDateStart', 'productionDateEnd', 'expirationDateStart',
  'expirationDateEnd', 'hasStock', 'warningDays', 'expirationStatus', 'movementType',
  'sourceBusinessType', 'sourceBusinessNo', 'movementDateStart', 'movementDateEnd',
  'operationUserKeyword',
])

export function parseAnimalCaseRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const outcome = queryInteger(query, 'treatmentOutcome', [1, 2, 3, 4, 5, 6, 99])
  const contagious = queryBoolean(query, 'isContagious')
  const keyEvent = queryBoolean(query, 'isKeyEvent')
  const dataSource = ['LOCAL', 'SHARED'].includes(queryText(query, 'dataSource', 10).toUpperCase())
    ? queryText(query, 'dataSource', 10).toUpperCase() : ''
  const caseNo = queryText(query, 'caseNo', 50)
  const start = queryDateTime(query, 'visitTimeStart')
  const end = queryDateTime(query, 'visitTimeEnd')
  return {
    filter: {
      caseNo,
      animalKeyword: queryText(query, 'animalKeyword', 100),
      visitTimeRange: start || end ? [start || end, end || start] : [],
      diseaseIdFk: queryText(query, 'diseaseIdFk', 32),
      attendingVetIdFk: queryText(query, 'attendingVetIdFk', 32),
      treatmentOutcome: outcome ?? '',
      isContagious: contagious ?? '',
      isKeyEvent: keyEvent ?? '',
      status: status ?? '',
      dataSource,
      latestConfirmedInChain: queryBoolean(query, 'latestConfirmedInChain') === true,
    },
    gridModel: compactModel({
      caseNo: textFilterModel(caseNo),
      treatmentOutcome: selectFilterModel(outcome),
      isContagious: selectFilterModel(contagious),
      isKeyEvent: selectFilterModel(keyEvent),
      status: selectFilterModel(status),
      dataSource: selectFilterModel(dataSource),
    }),
  }
}

export function parsePrescriptionRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const dispensingStatus = queryInteger(query, 'dispensingStatus', [1, 2, 3])
  const start = queryDateTime(query, 'prescriptionTimeStart')
  const end = queryDateTime(query, 'prescriptionTimeEnd')
  const filter = {
    prescriptionNo: queryText(query, 'prescriptionNo', 50),
    caseNo: queryText(query, 'caseNo', 50),
    animalKeyword: queryText(query, 'animalKeyword', 100),
    diagnosisName: queryText(query, 'diagnosisName', 100),
    prescribingVetIdFk: queryText(query, 'prescribingVetIdFk', 32),
    prescriptionTimeRange: start || end ? [start || end, end || start] : [],
    status: status ?? '',
    dispensingStatus: dispensingStatus ?? '',
    hasRemaining: queryBoolean(query, 'hasRemaining') === true,
  }
  return { filter, gridModel: compactModel({
    prescriptionNo: textFilterModel(filter.prescriptionNo),
    caseNo: textFilterModel(filter.caseNo),
    animalName: textFilterModel(filter.animalKeyword),
    diagnosisName: textFilterModel(filter.diagnosisName),
    prescribingVetName: selectFilterModel(filter.prescribingVetIdFk),
    prescriptionTime: dateFilterModel(start, end),
    status: selectFilterModel(status),
    dispensingStatus: selectFilterModel(dispensingStatus),
  }) }
}

export function parseDeathReportRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const start = queryDateTime(query, 'deathTimeStart')
  const end = queryDateTime(query, 'deathTimeEnd')
  const filter = {
    deathReportNo: queryText(query, 'deathReportNo', 50),
    animalKeyword: queryText(query, 'animalKeyword', 100),
    deathTimeRange: start || end ? [start || end, end || start] : [],
    caseNo: queryText(query, 'caseNo', 50),
    diseaseIdFk: queryText(query, 'diseaseIdFk', 32),
    attendingVetIdFk: queryText(query, 'attendingVetIdFk', 32),
    reportVetIdFk: queryText(query, 'reportVetIdFk', 32),
    status: status ?? '',
  }
  return { filter, gridModel: compactModel({
    deathReportNo: textFilterModel(filter.deathReportNo),
    caseNo: textFilterModel(filter.caseNo),
    status: selectFilterModel(status),
  }) }
}

export function parseInboundRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const start = queryDateTime(query, 'inboundTimeStart')
  const end = queryDateTime(query, 'inboundTimeEnd')
  const filter = {
    inboundRecordNo: queryText(query, 'inboundRecordNo', 50),
    inboundRecordName: queryText(query, 'inboundRecordName', 100),
    status: status ?? '',
    inboundUserIdFk: queryText(query, 'inboundUserIdFk', 32),
    inboundTimeRange: start || end ? [start || end, end || start] : [],
  }
  return { filter, gridModel: compactModel({
    inboundRecordNo: textFilterModel(filter.inboundRecordNo),
    inboundRecordName: textFilterModel(filter.inboundRecordName),
    inboundUserName: selectFilterModel(filter.inboundUserIdFk),
    inboundTime: dateFilterModel(start, end),
    status: selectFilterModel(status),
  }) }
}

export function parseOutboundRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const start = queryDateTime(query, 'outboundTimeStart')
  const end = queryDateTime(query, 'outboundTimeEnd')
  const filter = {
    outboundRecordNo: queryText(query, 'outboundRecordNo', 50),
    outboundRecordName: queryText(query, 'outboundRecordName', 100),
    prescriptionNo: queryText(query, 'prescriptionNo', 50),
    caseNo: queryText(query, 'caseNo', 50),
    animalKeyword: queryText(query, 'animalKeyword', 100),
    receiverUserIdFk: queryText(query, 'receiverUserIdFk', 32),
    dispenseUserIdFk: queryText(query, 'dispenseUserIdFk', 32),
    outboundTimeRange: start || end ? [start || end, end || start] : [],
    status: status ?? '',
  }
  return { filter, gridModel: compactModel({
    outboundRecordNo: textFilterModel(filter.outboundRecordNo),
    outboundRecordName: textFilterModel(filter.outboundRecordName),
    prescriptionNo: textFilterModel(filter.prescriptionNo),
    caseNo: textFilterModel(filter.caseNo),
    animalName: textFilterModel(filter.animalKeyword),
    receiverUserName: selectFilterModel(filter.receiverUserIdFk),
    dispenseUserName: selectFilterModel(filter.dispenseUserIdFk),
    outboundTime: dateFilterModel(start, end),
    status: selectFilterModel(status),
  }) }
}

export function parseAdjustmentRouteFilter(query = {}) {
  const status = queryInteger(query, 'status', [1, 99])
  const adjustmentType = queryInteger(query, 'adjustmentType', [1, 2, 3, 4, 99])
  const adjustmentTimeRange = queryDateTimeRange(
    query, 'adjustmentTimeStart', 'adjustmentTimeEnd'
  )
  const filter = {
    adjustmentRecordNo: queryText(query, 'adjustmentRecordNo', 50),
    adjustmentRecordName: queryText(query, 'adjustmentRecordName', 100),
    adjustmentType: adjustmentType ?? '',
    status: status ?? '',
    adjustmentUserIdFk: queryText(query, 'adjustmentUserIdFk', 32),
    adjustmentTimeRange,
  }
  return { filter, gridModel: compactModel({
    adjustmentRecordNo: textFilterModel(filter.adjustmentRecordNo),
    adjustmentRecordName: textFilterModel(filter.adjustmentRecordName),
    adjustmentType: selectFilterModel(adjustmentType),
    adjustmentUserName: selectFilterModel(filter.adjustmentUserIdFk),
    adjustmentTime: dateFilterModel(
      adjustmentTimeRange[0]?.slice(0, 10),
      adjustmentTimeRange[1]?.slice(0, 10)
    ),
    status: selectFilterModel(status),
  }) }
}

export function parseInventoryRouteFilter(query = {}) {
  const requestedTab = queryText(query, 'tab', 20)
  const tab = ['total', 'batch', 'expiration', 'movement'].includes(requestedTab) ? requestedTab : 'total'
  const common = {
    categoryIdFk: queryText(query, 'categoryIdFk', 32),
    medicineId: queryText(query, 'medicineId', 32),
  }
  if (tab === 'total') {
    const isEnable = queryBoolean(query, 'isEnable')
    const isLowStock = queryBoolean(query, 'isLowStock')
    const filter = { ...common, genericName: queryText(query, 'genericName', 100), isEnable: isEnable ?? '', isLowStock: isLowStock ?? '' }
    return { tab, filter, gridModel: compactModel({
      categoryName: selectFilterModel(filter.categoryIdFk), medicineName: selectFilterModel(filter.medicineId),
      genericName: textFilterModel(filter.genericName), isEnable: selectFilterModel(isEnable),
      stockStatusLabel: selectFilterModel(isLowStock),
    }) }
  }
  if (tab === 'batch') {
    const hasStock = queryBoolean(query, 'hasStock')
    const productionStart = queryDate(query, 'productionDateStart')
    const productionEnd = queryDate(query, 'productionDateEnd')
    const expirationStart = queryDate(query, 'expirationDateStart')
    const expirationEnd = queryDate(query, 'expirationDateEnd')
    const productionDateRange = normalizeVetRouteRange(productionStart, productionEnd)
    const expirationDateRange = normalizeVetRouteRange(expirationStart, expirationEnd)
    const filter = { ...common, batchNo: queryText(query, 'batchNo', 80),
      productionDateRange,
      expirationDateRange,
      hasStock: hasStock ?? '' }
    return { tab, filter, gridModel: compactModel({
      categoryName: selectFilterModel(filter.categoryIdFk), medicineName: selectFilterModel(filter.medicineId),
      batchNo: textFilterModel(filter.batchNo), productionDate: dateFilterModel(productionDateRange[0], productionDateRange[1]),
      expirationDate: dateFilterModel(expirationDateRange[0], expirationDateRange[1]), stockQuantity: selectFilterModel(hasStock),
    }) }
  }
  if (tab === 'expiration') {
    const warningDays = queryInteger(query, 'warningDays')
    const expirationStatus = ['EXPIRED', 'EXPIRING', 'NORMAL'].includes(queryText(query, 'expirationStatus', 20).toUpperCase())
      ? queryText(query, 'expirationStatus', 20).toUpperCase() : ''
    const filter = { ...common, batchNo: queryText(query, 'batchNo', 80),
      warningDays: warningDays != null && warningDays >= 0 && warningDays <= 3650 ? warningDays : 90,
      expirationStatus }
    return { tab, filter, gridModel: compactModel({
      categoryName: selectFilterModel(filter.categoryIdFk), medicineName: selectFilterModel(filter.medicineId),
      batchNo: textFilterModel(filter.batchNo), expirationStatusLabel: selectFilterModel(expirationStatus),
    }) }
  }
  const movementType = queryInteger(query, 'movementType', [1, 2, 3, 4, 5, 6, 99])
  const sourceType = ['INBOUND', 'OUTBOUND', 'STOCK_ADJUSTMENT'].includes(queryText(query, 'sourceBusinessType', 30).toUpperCase())
    ? queryText(query, 'sourceBusinessType', 30).toUpperCase() : ''
  const start = queryDate(query, 'movementDateStart')
  const end = queryDate(query, 'movementDateEnd')
  const movementDateRange = normalizeVetRouteRange(start, end)
  const filter = { ...common, batchNo: queryText(query, 'batchNo', 80), movementType: movementType ?? '',
    sourceBusinessType: sourceType, sourceBusinessNo: queryText(query, 'sourceBusinessNo', 50),
    movementDateRange,
    operationUserKeyword: queryText(query, 'operationUserKeyword', 80) }
  return { tab, filter, gridModel: compactModel({
    categoryName: selectFilterModel(filter.categoryIdFk), medicineName: selectFilterModel(filter.medicineId),
    batchNo: textFilterModel(filter.batchNo), movementTypeLabel: selectFilterModel(movementType),
    sourceBusinessTypeLabel: selectFilterModel(sourceType), sourceBusinessNo: textFilterModel(filter.sourceBusinessNo),
    movementTime: dateFilterModel(movementDateRange[0], movementDateRange[1]), operationUserName: textFilterModel(filter.operationUserKeyword),
  }) }
}

function compactModel(model) {
  return Object.fromEntries(Object.entries(model).filter(([, value]) => value != null))
}
